import { useState, useEffect } from "react";
import { Box, Typography, Button, useTheme, useMediaQuery } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useSelector } from "react-redux";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { sendCommandControl } from "../../../../services/apis";
import { useMutation } from "@tanstack/react-query";

import { selectUnitId } from "../../../../redux/Slice/SiteDirectory/SiteDirectorySlice";
import { AlertBar } from "../../../../components";
import { UnitsModelTableRow } from "../../../../types";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

export default function RebootUnitsModel({
  openRebootUnitsModel,
  setOpenRebootUnitsModel,
  showModelAlert,
  setShowModelAlert,
  selectedUnitsRows,
  selectedUnitsFilteredRows,
  setSelectedUnitsFilteredRows,
  setAlertState,
}: any) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const [isLoading, setIsLoading] = useState(false);
  const selectedUnitID = useSelector(selectUnitId);

  const columns: GridColDef<UnitsModelTableRow>[] = [
    {
      field: "unit_name",
      headerName: "Unit Name",
      minWidth: 150,
    },
    {
      field: "pace_device_serial_number",
      headerName: "PACE AI Device",
      minWidth: 150,
    },
  ];

  const handleClose = () => setOpenRebootUnitsModel(false);

  const bypassMutation = useMutation<number, Error, { control: string; units: string[] }>({
    mutationFn: ({ control, units }) => sendCommandControl(control, units),
    onSuccess: () => {
      setIsLoading(false);
      handleClose();
      setAlertState({
        isAlert: true,
        severity: "info",
        title: "",
        description: "PACE AI device on selected unit(s) waiting to be rebooted.",
        resetOpen: true,
      });
    },
    onError: (error) => {
      setIsLoading(false);
      handleClose();
      setAlertState({
        isAlert: true,
        severity: "error",
        title: "",
        description: "Failed to reboot  PACE AI device on selected unit(s). Please try again later.",
        resetOpen: true,
      });
    },
  });

  const handleSendCommand = () => {
    const serialNums: string[] = selectedUnitsRows.map((unit: any) => unit.pace_device_serial_number);
    setIsLoading(true);
    bypassMutation.mutate({ control: "reboot", units: serialNums });
  };

  useEffect(() => {
    if (selectedUnitsRows !== null && selectedUnitsRows !== undefined) {
      const updatedData: UnitsModelTableRow[] = [];
      for (const i in selectedUnitsRows) {
        const itemSub = selectedUnitsRows[i];
        const { id, unit_name, pace_ai_device_id, PACE_AIVersion, firmware } = itemSub;
        const sortedItem: UnitsModelTableRow = {
          id: id,
          unit_name: unit_name,
          pace_device_serial_number: pace_ai_device_id,
          PACE_AIVersion: PACE_AIVersion,
          firmware: firmware,
        };
        updatedData.push(sortedItem);
      }
      setSelectedUnitsFilteredRows(updatedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnitsRows]);

  return (
    <Dialog open={openRebootUnitsModel} onClose={handleClose}>
      <DialogTitle>
        <Typography id="modal-modal-title" variant="h6">
          REBOOT SELECTED UNIT(S)
        </Typography>
      </DialogTitle>
      <DialogContent>
        <AlertBar
          variant="standard"
          severity={"warning"}
          description={
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "700",
              }}
            >
              <strong>Warning:</strong>This action will restart the PACE AI devices for all selected units.
            </Typography>
          }
          show={showModelAlert}
          setShow={setShowModelAlert}
        />
        <Box sx={{ marginTop: "20px" }} />
        <Typography id="modal-modal-title" variant="body1">
          Please review your selection carefully before proceeding:{" "}
        </Typography>
        <Box sx={{ marginTop: "20px" }} />
        <DataGrid
          rows={selectedUnitsRows ?? []}
          columns={columns}
          hideFooter
          autoHeight
          sx={{
            display: "grid",
            "& .MuiDataGrid-cell": {
              border: 1,
              borderRight: 0,
              borderTop: 1,
              borderLeft: 0,
              borderColor: "#E2E9F4",
            },
          }}
          disableColumnFilter
          disableColumnMenu
        />
      </DialogContent>
      <DialogActions>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, marginTop: "20px" }}>
          <Button variant="text" color="primary" size="medium" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton loading={isLoading} variant="contained" onClick={handleSendCommand}>
            reboot units
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
